/**
 * Schemas der LLM-Ticket-Extraktion (Zielbild §5.2, §6.3).
 *
 * Die Ableitung ist das EINZIGE, was aus einem Ticketverlauf persistiert wird —
 * der Rohtext wird nach der Verarbeitung verworfen (E7). Entsprechend muss hier
 * alles stehen, was Wissenspool, Empfehlungen und Suche später brauchen.
 */
import { z } from "zod";

/** Ausprägung einer Anwendung beim Kunden — steuert die Übertragbarkeit (E6). */
export const APPLICATION_VARIANTS = ["standard", "eigenentwicklung", "unklar"] as const;

export const TICKET_CATEGORIES = [
  "fehler",
  "beratung",
  "change",
  "berechtigung",
  "schnittstelle",
  "sonstiges",
] as const;

const confidence = z.number().min(0).max(1);

export const extractedApplicationSchema = z.object({
  /** Katalog-Ebene, z. B. „ESS Urlaubsantrag" — nicht kundenspezifisch benannt. */
  name: z.string().min(1).max(120),
  /** Produkt oder Modul darüber, z. B. „SAP HCM". */
  product: z.string().min(1).max(120),
  variant: z.enum(APPLICATION_VARIANTS),
  confidence,
  /**
   * Wörtlicher Beleg aus dem Eingabetext. Der Output-Validator prüft, dass
   * dieser Textteil wirklich in der Eingabe vorkommt — das ist die
   * Halluzinations-Bremse.
   */
  evidence: z.string().min(1).max(300),
});

/**
 * Bereinigt eine Bezeichner-Liste VOR der eigentlichen Zod-Prüfung, statt bei
 * einem einzelnen kaputten Eintrag die GESAMTE Ableitung zu verwerfen
 * (Befund 2, Sonden-Probelauf gegen die Jira-Sandbox: ein Ticket ging
 * komplett verloren, weil das Modell in `identifiers.exceptions[0]` einen
 * ganzen Satz statt eines Bezeichners lieferte und die `max(80)`-Prüfung
 * daraufhin problem/solution/applications gleich mit ablehnte).
 *
 * Nachsicht gilt NUR hier — für `identifiers` — und NUR für Länge/Leerraum:
 * Einträge werden getrimmt, leere entfernt, zu lange ausgesondert (ein
 * Bezeichner, der länger ist als das erlaubte Maximum, ist Fließtext, kein
 * Bezeichner). Nicht-String-Einträge werden unverändert an das Schema
 * durchgereicht, damit ein echter Typfehler weiterhin wie gewohnt hart
 * auffliegt — nur die Länge wird nachsichtig behandelt. `maxLength` ist
 * dieselbe Zahl wie im `.max()` der Feld-Schema direkt darunter (eine
 * Quelle der Wahrheit statt zweier auseinanderlaufender Grenzwerte) — bei
 * Änderung dort IMMER auch hier mit anpassen.
 *
 * Alle anderen Felder (insbesondere `evidence`, an dem die
 * Halluzinations-Prüfung hängt) bleiben bewusst strikt und bekommen KEINE
 * solche Vorverarbeitung.
 */
const MAX_IDENTIFIERS = 30;

function identifierListSchema(maxLength: number): z.ZodType<string[], z.ZodTypeDef, string[]> {
  const preprocessed = z.preprocess((value) => {
    if (!Array.isArray(value)) return value;
    const cleaned = value.flatMap((entry) => {
      if (typeof entry !== "string") return [entry];
      const trimmed = entry.trim();
      if (trimmed.length === 0 || trimmed.length > maxLength) return [];
      return [trimmed];
    });
    // Auch die ANZAHL wird nachsichtig behandelt: Ein Ticket mit mehr als
    // dreißig Code-Objekten (Sonde 2026-07-28: MS-1548, ein langer
    // Entwicklungsverlauf) verlor sonst Anwendungen, Problem und Lösung
    // gleich mit, nur weil die Bezeichnerliste überlief. Die ersten dreißig
    // reichen als Beleg; der Rest fällt weg.
    return cleaned.slice(0, MAX_IDENTIFIERS);
  }, z.array(z.string().max(maxLength)).max(MAX_IDENTIFIERS));

  // `z.preprocess` typisiert seinen Input laut Zod IMMER als `unknown` (die
  // Vorverarbeitung soll ja gerade roh-typisierte externe Daten annehmen
  // dürfen) — das lässt `ticketExtractionSchema` strukturell auseinanderlaufen
  // von `ZodType<TicketExtraction>` (Input=Output vorausgesetzt, s.
  // `CompleteArgs<T>` in packages/llm/src/provider.ts). Der eigentliche
  // `.parse()`-Aufruf bekommt ohnehin immer bereits geparstes JSON (`unknown`)
  // übergeben — der Cast ändert daran nichts, er stellt nur den nach außen
  // sichtbaren Typ wieder auf Input=Output=`string[]`.
  return preprocessed as unknown as z.ZodType<string[], z.ZodTypeDef, string[]>;
}

export const ticketExtractionSchema = z.object({
  applications: z.array(extractedApplicationSchema).max(5),
  problem: z.string().min(1).max(600),
  /** Leer erlaubt: nicht jedes Ticket dokumentiert eine Lösung. */
  solution: z.string().max(1200),
  category: z.enum(TICKET_CATEGORIES),
  identifiers: z.object({
    transports: identifierListSchema(40),
    codeObjects: identifierListSchema(80),
    exceptions: identifierListSchema(80),
  }),
  confidence,
});

export type ExtractedApplication = z.infer<typeof extractedApplicationSchema>;
export type TicketExtraction = z.infer<typeof ticketExtractionSchema>;

/** Eingabe-Schema (Guardrail 1) — was der Extraktions-Anwendungsfall annimmt. */
export const extractionInputSchema = z.object({
  ticketKey: z.string().regex(/^[A-Z][A-Z0-9]+-\d+$/),
  /** Bereits redigierter Text. Roh-PII darf hier nicht mehr enthalten sein. */
  text: z.string().min(20).max(60_000),
});

export type ExtractionInput = z.infer<typeof extractionInputSchema>;
